/**
 * Topture Custom Mega Menu — JavaScript
 * Handles: desktop hover/keyboard, template cloning, mobile drawer/drill-down,
 *          tablet touch support, analytics tracking
 *
 * LAZY RENDERING: The mobile drawer DOM is NOT in the initial HTML. On the first
 * hamburger tap, we read the JSON data blob from #mm-mobile-data, build the full
 * panel tree, inject it into .mm-drawer__content, then bind all event listeners.
 * Subsequent taps just toggle the drawer open/closed — no rebuild.
 *
 * No jQuery dependency — vanilla JS only
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  let hoverTimeout = null;
  let closeTimeout = null;
  let openTimeout = null;

  // Hover-intent timings — tuned for older mouse users / trackpad imprecision.
  // Open delay debounces accidental drift; close delay tolerates diagonal travel
  // from a left-column tab toward the right-side panel without snapping shut.
  const MEGA_OPEN_DELAY = 200;
  const MEGA_CLOSE_DELAY = 450;
  // Tab-switch delays. The "submenu direction" delay gives the cursor time to
  // travel toward the right panel without category-switching when it briefly
  // crosses a neighboring left-column tab (the "safe triangle" pattern).
  const TAB_HOVER_DELAY = 80;
  const TAB_SUBMENU_DIRECTION_DELAY = 350;

  /* ============================================================
     MENU SESSION STATE (used by Analytics + close events)
     ============================================================ */

  const MENU_FALLBACK_SEARCH_WINDOW_MS = 30000;
  const MAX_IMPRESSIONS = 50;

  const L1_LABEL_BY_KEY = {
    saunas: 'Saunas',
    heaters: 'Sauna Heaters',
    materials: 'Sauna Materials',
    wellness: 'Wellness'
  };
  const CTA_TYPE_BY_L1_KEY = {
    saunas: 'quiz',
    heaters: 'calculator',
    materials: null,
    wellness: null
  };

  let currentMenuSessionId = null;
  let menuSessionStartedAt = null;
  let currentMenuL1Key = null;          // 'saunas' | 'heaters' | 'wellness'
  let clickedDuringSession = false;
  let lastMenuCloseAt = null;
  let lastMenuCloseHadNoClick = null;

  // Review observer state
  const seenReviews = new Set();
  let reviewObserver = null;

  /** Generate a fresh menu_session_id. */
  function generateSessionId() {
    return 'ms_' + Date.now().toString(36) + '_' +
           Math.random().toString(36).slice(2, 8);
  }

  /** Strip query string + hash from a URL for canonical mm_link_url. */
  function stripQuery(url) {
    if (!url || typeof url !== 'string') return url;
    const qIdx = url.indexOf('?');
    const hIdx = url.indexOf('#');
    let end = url.length;
    if (qIdx !== -1) end = Math.min(end, qIdx);
    if (hIdx !== -1) end = Math.min(end, hIdx);
    return url.slice(0, end);
  }

  /** Device classification: 'desktop' | 'tablet' | 'mobile'. */
  function getDeviceLabel() {
    // Mobile drawer presence is the strongest mobile signal
    const drawer = document.querySelector('[data-mobile-drawer]');
    const isMobileViewport = window.innerWidth < 1000;
    if (isMobileViewport && drawer) return 'mobile';
    return hasHoverSupport() ? 'desktop' : 'tablet';
  }

  function l1LabelFromKey(key) {
    if (!key) return null;
    return L1_LABEL_BY_KEY[key] || key;
  }

  function startMenuSession(l1Key) {
    currentMenuSessionId = generateSessionId();
    menuSessionStartedAt = Date.now();
    currentMenuL1Key = l1Key || null;
    clickedDuringSession = false;
  }

  function clearMenuSessionState(hadClick) {
    lastMenuCloseAt = Date.now();
    lastMenuCloseHadNoClick = !hadClick;
    currentMenuSessionId = null;
    menuSessionStartedAt = null;
    currentMenuL1Key = null;
    clickedDuringSession = false;
  }

  /**
   * Fire close event for an active menu session (panel close OR drawer close).
   * No-op if no session is active (safe to call from close handlers that may
   * run after a sync close-on-click has already cleared state).
   */
  function endMenuSession() {
    if (!currentMenuSessionId) return;
    const dur = menuSessionStartedAt ? Date.now() - menuSessionStartedAt : null;
    const hadClick = clickedDuringSession === true;
    const l1Label = l1LabelFromKey(currentMenuL1Key);
    Analytics.track(hadClick ? 'menu_close_with_click' : 'menu_close_no_click', {
      mm_l1: l1Label,
      mm_device: getDeviceLabel(),
      duration_ms: dur
    });
    clearMenuSessionState(hadClick);
  }

  /**
   * Sync close fired BEFORE page navigation (link click / CTA click / cart /
   * account / logo / footer click while menu is open). Listener uses sendBeacon
   * which survives unload.
   */
  function fireSyncCloseWithClick() {
    if (!currentMenuSessionId) return;
    const dur = menuSessionStartedAt ? Date.now() - menuSessionStartedAt : null;
    const l1Label = l1LabelFromKey(currentMenuL1Key);
    Analytics.track('menu_close_with_click', {
      mm_l1: l1Label,
      mm_device: getDeviceLabel(),
      duration_ms: dur
    });
    clearMenuSessionState(true);
  }

  /** Mark current session as having had at least one engaged click. */
  function markClick() {
    if (currentMenuSessionId) clickedDuringSession = true;
  }

  /**
   * Detect if the device supports hover (true pointer like a mouse).
   *
   * Uses `any-hover` / `any-pointer` rather than `hover` / `pointer` so that
   * devices with MULTIPLE input methods (touchscreen laptops, hybrid tablets
   * with a mouse plugged in) still get hover behavior when a mouse is
   * present. The non-`any-` queries report only the PRIMARY pointer's
   * capabilities, which the OS often reports as coarse/touch on hybrid
   * devices — leaving the user with click-only menus even when they
   * actually have a mouse.
   *
   * Falls back to touch detection for browsers without matchMedia.
   */
  function hasHoverSupport() {
    if (window.matchMedia) {
      return window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches;
    }
    return !('ontouchstart' in window);
  }

  /** Create an element with attributes and optional children */
  function h(tag, attrs, ...children) {
    const el = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'className') el.className = v;
        else if (k === 'innerHTML') el.innerHTML = v;
        else if (k === 'textContent') el.textContent = v;
        else if (k.startsWith('data-') || k.startsWith('aria-') || k === 'role' || k === 'hidden' || k === 'tabindex') {
          if (v === true) el.setAttribute(k, '');
          else if (v !== false && v != null) el.setAttribute(k, v);
        }
        else el.setAttribute(k, v);
      });
    }
    children.forEach(child => {
      if (typeof child === 'string') el.appendChild(document.createTextNode(child));
      else if (child) el.appendChild(child);
    });
    return el;
  }

  /** Create an SVG element from raw markup string */
  function svg(html) {
    const t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }

  /* Reusable SVG strings */
  const SVG_CLOSE = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  const SVG_CHEVRON_RIGHT = '<svg class="mm-chevron-right" width="10" height="16" viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M1.5 1l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SVG_CHEVRON_RIGHT_SM = '<svg class="mm-chevron-right" width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M1.5 1l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SVG_BACK = '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M6.5 1l-5 5 5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';


  /* ============================================================
     ANALYTICS: Menu interaction tracking
     Replaces the removed ANG Extra Menu analytics.js.
     Fires custom DOM events + pushes to dataLayer (GA4) if available.
     ============================================================ */

  const Analytics = {
    /**
     * Track a menu interaction. Auto-injects menu_session_id when a session
     * is active so all events in one exploration ritual share an id. Fires a
     * CustomEvent on document AND pushes to window.dataLayer for GA4/GTM if
     * present (legacy GTM paths are bit-for-bit unchanged).
     *
     * The topture-tracking listener for `mm:analytics` is required to use
     * navigator.sendBeacon (with fetch keepalive fallback) for its network
     * send — both purpose-built to survive page unload. fetch/XHR/async-await
     * would be cancelled mid-flight when the link navigates.
     *
     * @param {string} action   - e.g. 'mega_open', 'tab_switch', 'link_click'
     * @param {Object} details  - event payload
     */
    track(action, details) {
      const enriched = currentMenuSessionId
        ? Object.assign({}, details, { menu_session_id: currentMenuSessionId })
        : (details || {});

      // Custom DOM event (for first-party topture-tracking listener)
      document.dispatchEvent(new CustomEvent('mm:analytics', {
        detail: Object.assign({ action: action }, enriched)
      }));

      // GA4 via dataLayer (GTM) — if available. Shape unchanged from legacy.
      if (window.dataLayer) {
        window.dataLayer.push(Object.assign({
          event: 'mega_menu',
          mm_action: action
        }, enriched));
      }

      // GA4 via gtag — if available and no dataLayer. Shape unchanged.
      if (!window.dataLayer && typeof window.gtag === 'function') {
        window.gtag('event', action, Object.assign({
          event_category: 'mega_menu'
        }, enriched));
      }

      // Microsoft Clarity custom events — fire ONLY on a true "menu open",
      // split desktop vs mobile by the actual element that opened. Surfaces in
      // Clarity → Filters → Events for segmenting recordings.
      //   desktop nav mega panel opened  → menu_open_desktop  (mega_open, non-mobile)
      //   mobile hamburger drawer opened → menu_open_mobile   (mobile_drawer_open)
      // The in-drawer L1 drill also fires mega_open with mm_device:'mobile' — it's
      // a secondary open INSIDE an already-open drawer, so it's deliberately excluded
      // (the drawer-open above already counted it). Once-per-open is inherited from
      // openMegaPanel's already-open no-op guard and openDrawer's single open path —
      // no extra debounce needed. Guarded so it's a no-op if Clarity is absent/blocked.
      if (window.clarity) {
        if (action === 'mega_open' && enriched.mm_device !== 'mobile') {
          window.clarity('event', 'menu_open_desktop');
        } else if (action === 'mobile_drawer_open') {
          window.clarity('event', 'menu_open_mobile');
        }
      }
    }
  };


  /* ============================================================
     DESKTOP: MEGA PANEL OPEN / CLOSE
     ============================================================ */

  function initDesktopMega() {
    const triggers = $$('[data-mega-trigger]');
    if (!triggers.length) return;

    const useHover = hasHoverSupport();

    triggers.forEach(trigger => {
      const key = trigger.dataset.megaTrigger;
      const panel = $(`[data-mega-panel="${key}"]`);
      if (!panel) return;

      const navItem = trigger.closest('.mm-nav-item');

      if (useHover) {
        // MOUSE DEVICES: Open on hover with intent delay; close with grace period.
        // The open delay debounces accidental drift past the trigger when the
        // user is heading elsewhere on the nav. The close grace tolerates
        // diagonal cursor travel from a left tab to the right panel.
        navItem.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          clearTimeout(openTimeout);
          // If panel is already open (e.g. cursor briefly left then returned),
          // skip the open delay so the menu doesn't flicker.
          if (panel.classList.contains('mm-mega--open')) {
            openMegaPanel(trigger, panel);
          } else {
            openTimeout = setTimeout(() => {
              openMegaPanel(trigger, panel);
            }, MEGA_OPEN_DELAY);
          }
        });

        navItem.addEventListener('mouseleave', () => {
          clearTimeout(openTimeout);
          closeTimeout = setTimeout(() => {
            closeMegaPanel(trigger, panel);
          }, MEGA_CLOSE_DELAY);
        });

        panel.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          clearTimeout(openTimeout);
        });

        panel.addEventListener('mouseleave', () => {
          closeTimeout = setTimeout(() => {
            closeMegaPanel(trigger, panel);
          }, MEGA_CLOSE_DELAY);
        });
      } else {
        // TOUCH DEVICES (tablets): Toggle on tap
        // Prevent the link from navigating on first tap — open the mega instead.
        // Second tap on the same trigger follows the link.
        trigger.addEventListener('click', (e) => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            // Already open — let the click navigate to the collection page
            return;
          }
          e.preventDefault();
          closeAllMegaPanels();
          openMegaPanel(trigger, panel);
        });

        // Outside-tap close is registered ONCE below (shared with the hover
        // branch) — registering here per-trigger stacked N identical
        // never-removed document listeners.
      }

      // Keyboard: Enter/Space to toggle (all devices)
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            closeMegaPanel(trigger, panel);
          } else {
            closeAllMegaPanels();
            openMegaPanel(trigger, panel);
            const firstTab = $('[role="tab"]', panel);
            if (firstTab) firstTab.focus();
          }
        }

        if (e.key === 'Escape') {
          closeMegaPanel(trigger, panel);
          trigger.focus();
        }
      });
    });

    // Escape from anywhere inside a panel
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openPanel = $('.mm-mega--open');
        if (openPanel) {
          const key = openPanel.dataset.megaPanel;
          const trigger = $(`[data-mega-trigger="${key}"]`);
          closeMegaPanel(trigger, openPanel);
          if (trigger) trigger.focus();
        }
      }
    });

    // Mouse devices: close when clicking outside the mega panel.
    //
    // DEFERRED via setTimeout(0): outside-click is delegated on document with
    // bubble-phase, same as the tracked-click handlers (cart, account, logo,
    // mobile_drawer_footer). All bubble-phase listeners fire in registration
    // order, and initDesktopMega registers FIRST — so without the defer,
    // closeAllMegaPanels would run before the cart/logo/account handler had a
    // chance to mark the click as engaged. That race fires menu_close_no_click
    // instead of menu_close_with_click, misclassifying engaged navigation as
    // drop-off. Deferring the close to the next macrotask lets the tracked
    // handler run first, set clickedDuringSession=true, and fire its own
    // sync close_with_click; closeAllMegaPanels then runs as a no-op for
    // analytics (session already cleared) and only does the visual close.
    // Registered once for BOTH hover and touch devices (the touch branch used
    // to register this per-trigger — same handler, N accumulated listeners).
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mm-nav-item--mega') && !e.target.closest('.mm-mega')) {
        setTimeout(closeAllMegaPanels, 0);
      }
    });
  }

  function openMegaPanel(trigger, panel) {
    // No-op if this exact panel is already open (prevents double-fire on
    // rapid hover-out/hover-in within the 200ms close debounce window).
    if (panel.classList.contains('mm-mega--open')) return;

    // Close any other open panel (fires its close event via closeMegaPanel)
    closeAllMegaPanels();

    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('mm-mega--open');

    // The default tab's columns are deferred like every other tab's (so the
    // server HTML carries no L4 links sitewide), and activateTab() never runs
    // for it on open. Hydrate it here, BEFORE fireDesktopImpressions reads it.
    const defaultSubPanel = $('.mm-panel--active', panel);
    if (defaultSubPanel) cloneTemplates(defaultSubPanel);

    const menuKey = trigger.dataset.megaTrigger;
    const l1Label = l1LabelFromKey(menuKey);
    const device = getDeviceLabel();

    // Start a fresh session for this L1 panel
    startMenuSession(menuKey);

    // Fire mega_open (session_id auto-injects)
    Analytics.track('mega_open', {
      mm_menu: menuKey,        // legacy field — preserved for existing GTM tags
      mm_l1: l1Label,
      mm_device: device
    });

    // Fire impressions for the initially-visible L2 tab
    fireDesktopImpressions(panel, menuKey);
  }

  function closeMegaPanel(trigger, panel) {
    if (!trigger || !panel) return;
    // Only fire close analytics if THIS panel is the active session's panel
    const panelKey = panel.dataset.megaPanel;
    const isActiveSessionPanel = panelKey && panelKey === currentMenuL1Key;

    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('mm-mega--open');

    if (isActiveSessionPanel) {
      endMenuSession();
    }
  }

  function closeAllMegaPanels() {
    // Close the currently-open session-tracked panel FIRST (with analytics)
    const openPanel = $('.mm-mega--open');
    if (openPanel) {
      const key = openPanel.dataset.megaPanel;
      const trigger = $(`[data-mega-trigger="${key}"]`);
      if (trigger) {
        closeMegaPanel(trigger, openPanel);
      } else {
        openPanel.classList.remove('mm-mega--open');
      }
    }
    // Defensive sweep for any stragglers (no analytics — no session attached)
    $$('[data-mega-trigger]').forEach(t => {
      t.setAttribute('aria-expanded', 'false');
    });
    $$('.mm-mega--open').forEach(p => {
      p.classList.remove('mm-mega--open');
    });
  }


  /* ============================================================
     DESKTOP: TAB SWITCHING + TEMPLATE CLONING
     ============================================================ */

  function initDesktopTabs() {
    const tabLists = $$('[role="tablist"]');

    tabLists.forEach(tabList => {
      const tabs = $$('[role="tab"]', tabList);
      const mega = tabList.closest('.mm-mega');
      if (!mega) return;

      // Safe-triangle state — tracks recent cursor movement inside the mega
      // panel so we can detect "user is traveling toward the right panel" and
      // suppress category-switching during that transit.
      const content = $('.mm-content', mega) || mega;
      const cursor = { x: 0, y: 0, px: 0, py: 0 };
      if (hasHoverSupport()) {
        mega.addEventListener('mousemove', (e) => {
          cursor.px = cursor.x;
          cursor.py = cursor.y;
          cursor.x = e.clientX;
          cursor.y = e.clientY;
        });
        // Clear hover timer if the cursor reaches the right panel — they made it.
        content.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
        });
      }

      // Returns true if the cursor's recent movement vector points into the
      // bounding triangle formed by (previous cursor position) → (top-right of
      // the right panel) → (bottom-right of the right panel). That's the
      // angular cone in which the cursor is plausibly traveling toward the
      // panel rather than aiming at a different left-column tab.
      function isMovingTowardSubmenu() {
        const dx = cursor.x - cursor.px;
        if (dx <= 0) return false; // not moving rightward at all
        const submenu = content.getBoundingClientRect();
        // Compare slope of cursor movement against slopes from previous
        // cursor position to top-right and bottom-right of the right panel.
        const slopeUpper = (submenu.top    - cursor.py) / (submenu.right - cursor.px);
        const slopeLower = (submenu.bottom - cursor.py) / (submenu.right - cursor.px);
        const slopeMove  = (cursor.y - cursor.py)      / dx;
        return slopeMove >= slopeUpper && slopeMove <= slopeLower;
      }

      tabs.forEach(tab => {
        // Hover: switch tab (mouse devices)
        if (hasHoverSupport()) {
          tab.addEventListener('mouseenter', () => {
            clearTimeout(hoverTimeout);
            // If the cursor is heading toward the right-side submenu, use a
            // longer delay so the user has time to reach the panel without
            // their in-transit tab being "selected" by accident. Otherwise
            // (they're settling on this tab deliberately) use the short delay.
            const delay = isMovingTowardSubmenu()
              ? TAB_SUBMENU_DIRECTION_DELAY
              : TAB_HOVER_DELAY;
            hoverTimeout = setTimeout(() => {
              activateTab(tab, mega);
            }, delay);
          });
        } else {
          // Touch: switch tab on tap
          tab.addEventListener('click', (e) => {
            e.preventDefault();
            activateTab(tab, mega);
          });
        }

        // Click: follow the tab's destination (mouse devices). Bail on
        // modifier/middle clicks — the browser handles new-tab natively; a
        // manual location.href here would ALSO navigate the current page away.
        //
        // The URL now comes from data-tab-url on the button. It used to come
        // from a nested <a>, which was invalid markup (an interactive element
        // inside a button — axe "nested-interactive"). That anchor was already
        // `pointer-events: none`, so the button was always the real click
        // target and this path is unchanged in behaviour.
        if (hasHoverSupport()) {
          tab.addEventListener('click', (e) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            const url = tab.dataset.tabUrl;
            if (!url || url === '#') return;
            window.location.href = url;
          });
        }

        // Keyboard navigation (all devices)
        tab.addEventListener('keydown', (e) => {
          const currentIndex = tabs.indexOf(tab);
          let newIndex = currentIndex;

          switch (e.key) {
            case 'ArrowDown':
              e.preventDefault();
              newIndex = Math.min(currentIndex + 1, tabs.length - 1);
              break;
            case 'ArrowUp':
              e.preventDefault();
              newIndex = Math.max(currentIndex - 1, 0);
              break;
            case 'Home':
              e.preventDefault();
              newIndex = 0;
              break;
            case 'End':
              e.preventDefault();
              newIndex = tabs.length - 1;
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              // data-tab-url, not a nested <a> — see the click handler above.
              const tabUrl = tab.dataset.tabUrl;
              if (tabUrl && tabUrl !== '#') {
                window.location.href = tabUrl;
              }
              return;
            case 'Tab':
              return;
            default:
              return;
          }

          if (newIndex !== currentIndex) {
            activateTab(tabs[newIndex], mega);
            tabs[newIndex].focus();
          }
        });
      });
    });
  }

  function activateTab(tab, mega) {
    const panelId = tab.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);
    if (!panel) return;

    // Deactivate all tabs in this tablist
    const tabList = tab.closest('[role="tablist"]');
    $$('[role="tab"]', tabList).forEach(t => {
      t.classList.remove('mm-tab--active');
      t.setAttribute('aria-selected', 'false');
    });

    // Deactivate all panels in this mega
    const content = $('.mm-content', mega);
    $$('.mm-panel', content).forEach(p => {
      p.classList.remove('mm-panel--active');
      p.setAttribute('hidden', '');
    });

    // Activate the selected tab + panel
    tab.classList.add('mm-tab--active');
    tab.setAttribute('aria-selected', 'true');
    panel.classList.add('mm-panel--active');
    panel.removeAttribute('hidden');

    // Clone deferred templates if not already done
    cloneTemplates(panel);

    // Hero images on inactive tabs are parked as data-hero-src — see the hero
    // block in mega-menu-desktop.liquid for why they are not lazy <img>.
    panel.querySelectorAll('.mm-tab-hero__img--pending[data-hero-src]').forEach((ph) => {
      const img = document.createElement('img');
      img.src = ph.dataset.heroSrc;
      img.alt = '';
      img.width = 280;
      img.height = 148;
      img.className = 'mm-tab-hero__img';
      ph.replaceWith(img);
    });

    // Analytics — same session_id (tab_switch is one exploration ritual)
    const tabLink = $('.mm-tab-link', tab);
    const tabName = tabLink ? tabLink.textContent.trim() : tab.textContent.trim();
    const menuKey = mega.dataset.megaPanel;
    const l1Label = l1LabelFromKey(menuKey);
    Analytics.track('tab_switch', {
      mm_menu: menuKey,        // legacy
      mm_tab: tabName,         // legacy
      mm_tab_index: tab.dataset.tabIndex,  // legacy
      mm_l1: l1Label,
      mm_l2: tabName,
      mm_device: getDeviceLabel()
    });

    // Fire impressions for the newly-visible L2 tab content
    fireDesktopImpressions(mega, menuKey);
  }

  /* ============================================================
     IMPRESSION TRACKING — desktop + mobile
     ============================================================ */

  /**
   * Snapshot all visible L4 links + sidebar CTA in the active L2 tab of a
   * desktop mega panel and fire `menu_impressions`.
   */
  function fireDesktopImpressions(panel, menuKey) {
    if (!panel) return;
    const activeSubPanel = $('.mm-panel--active', panel);
    if (!activeSubPanel) return;

    const l1Label = l1LabelFromKey(menuKey);
    const activeTab = $('[role="tab"][aria-selected="true"]', panel);
    const tabLink = activeTab ? $('a', activeTab) : null;
    const l2Label = tabLink ? tabLink.textContent.trim()
                           : (activeTab ? activeTab.textContent.trim() : null);

    const impressions = [];
    const seen = new Set();

    $$('.mm-column', activeSubPanel).forEach(col => {
      if (impressions.length >= MAX_IMPRESSIONS) return;
      const titleEl = $('.mm-column-title', col)  /* tag-agnostic: the heading level is h4 now, was h3 */;
      const colTitle = titleEl ? titleEl.textContent.trim() : null;
      $$('a', col).forEach(a => {
        if (impressions.length >= MAX_IMPRESSIONS) return;
        const rawUrl = a.getAttribute('href') || '';
        const url = stripQuery(rawUrl);
        const key = (colTitle || '') + '||' + url;
        if (seen.has(key)) return;
        seen.add(key);
        impressions.push({
          mm_column: colTitle,
          mm_link_text: (a.textContent || '').trim(),
          mm_link_url: url
        });
      });
    });

    // Also include category-image cards (visual L4 alternatives)
    $$('.mm-category-card', activeSubPanel).forEach(card => {
      if (impressions.length >= MAX_IMPRESSIONS) return;
      const labelEl = $('.mm-category-card__label', card);
      const text = labelEl ? labelEl.textContent.trim()
                          : (card.getAttribute('aria-label') || card.textContent.trim());
      const url = stripQuery(card.getAttribute('href') || '');
      const key = 'category_image||' + url;
      if (seen.has(key)) return;
      seen.add(key);
      impressions.push({
        mm_column: 'category_image',
        mm_link_text: text,
        mm_link_url: url
      });
    });

    // Sidebar CTA (Quiz / Calculator) — lives in panel sidebar, not in active subpanel
    if (impressions.length < MAX_IMPRESSIONS) {
      const cta = $('.mm-sidebar-cta', panel);
      if (cta) {
        const labelEl = $('.mm-sidebar-cta__label', cta);
        const text = labelEl ? labelEl.textContent.trim() : cta.textContent.trim();
        const url = stripQuery(cta.getAttribute('href') || '');
        impressions.push({
          mm_column: 'sidebar',
          mm_link_text: text,
          mm_link_url: url,
          cta_type: CTA_TYPE_BY_L1_KEY[menuKey] || null
        });
      }
    }

    Analytics.track('menu_impressions', {
      mm_l1: l1Label,
      mm_l2: l2Label,
      mm_device: getDeviceLabel(),
      impressions: impressions
    });
  }

  /**
   * Snapshot the drawer-root product carousel (visible on drawer open
   * BEFORE any drill) and fire `menu_impressions`. Without this the
   * carousel has a NULL CTR denominator. mm_l1 is null on root — the
   * dashboard buckets it as '(drawer root)'.
   */
  function fireDrawerRootImpressions(drawerEl) {
    const impressions = [];
    $$('.mm-mobile-products--root .mm-mobile-product-card', drawerEl).forEach(card => {
      if (impressions.length >= MAX_IMPRESSIONS) return;
      const titleEl = $('.mm-mobile-product-card__title', card);
      const text = titleEl ? titleEl.textContent.trim()
                          : (card.textContent || '').replace(/\s+/g, ' ').trim();
      const url = stripQuery(card.getAttribute('href') || '');
      impressions.push({
        mm_section: 'drawer_root',
        mm_link_text: text,
        mm_link_url: url
      });
    });
    Analytics.track('menu_impressions', {
      mm_l1: null,
      mm_l2: null,
      mm_device: 'mobile',
      impressions: impressions
    });
  }

  /**
   * Snapshot all L4 links + Best Sellers in a mobile L2 panel and fire
   * `menu_impressions`. Called when an L2 panel becomes visible.
   */
  function fireMobileImpressions(panel) {
    if (!panel) return;
    const l1Label = panel.dataset.l1Label || null;
    const l2Label = panel.dataset.l2Label || null;

    const impressions = [];
    const seen = new Set();

    // L4 links inside a group + "Shop All" links. Selected by MEANING
    // (data-mm-impression-group), not by a presentation class — the old
    // '.mm-mobile-accordion-content' selector would have died silently when
    // the accordion markup went away.
    $$('[data-mm-impression-group]', panel).forEach(group => {
      if (impressions.length >= MAX_IMPRESSIONS) return;
      // Prefer the visible heading text so mm_column keeps the same human
      // value it has always reported; the slug attribute is the fallback.
      const heading = group.previousElementSibling;
      const colTitle = (heading && heading.classList.contains('mm-mobile-group__title'))
        ? (heading.textContent || '').replace(/\s+/g, ' ').trim()
        : (group.getAttribute('data-mm-impression-group') || null);

      $$('a.mm-mobile-link', group).forEach(a => {
        if (impressions.length >= MAX_IMPRESSIONS) return;
        const url = stripQuery(a.getAttribute('href') || '');
        const key = (colTitle || '') + '||' + url;
        if (seen.has(key)) return;
        seen.add(key);
        impressions.push({
          mm_column: colTitle,
          mm_link_text: (a.textContent || '').trim(),
          mm_link_url: url
        });
      });
    });

    // Best Sellers product cards above the columns
    $$('a.mm-mobile-product-card', panel).forEach(card => {
      if (impressions.length >= MAX_IMPRESSIONS) return;
      const titleEl = $('.mm-mobile-product-card__title', card);
      const text = titleEl ? titleEl.textContent.trim() : (card.textContent || '').trim();
      const url = stripQuery(card.getAttribute('href') || '');
      const key = 'best_sellers||' + url;
      if (seen.has(key)) return;
      seen.add(key);
      impressions.push({
        mm_section: 'best_sellers',
        mm_link_text: text,
        mm_link_url: url
      });
    });

    Analytics.track('menu_impressions', {
      mm_l1: l1Label,
      mm_l2: l2Label,
      mm_device: 'mobile',
      impressions: impressions
    });
  }

  function cloneTemplates(panel) {
    const templates = $$('template.mm-deferred-links', panel);
    templates.forEach(tmpl => {
      const clone = tmpl.content.cloneNode(true);
      tmpl.parentNode.insertBefore(clone, tmpl);
      tmpl.remove();
    });
  }


  /* ============================================================
     DESKTOP + MOBILE: LINK CLICK TRACKING
     ============================================================ */

  function initLinkTracking() {
    // ── DESKTOP ──────────────────────────────────────────────
    // L2 tab links + L4 links inside columns + category image cards.
    // EXCLUDES .mm-sidebar-cta (handled by initSidebarCtaTracking — see dedup
    // rule: never fire both link_click and sidebar_cta_click for the same el).
    document.addEventListener('click', (e) => {
      // .mm-tab is the L2 tab BUTTON. It used to be matched as
      // `a.mm-tab-link` — the nested anchor that was removed to fix
      // axe nested-interactive. Its destination now lives on data-tab-url,
      // which is why the url read below falls back to the dataset.
      const link = e.target.closest(
        '.mm-mega .mm-tab, ' +
        '.mm-mega .mm-link-list a, ' +
        '.mm-mega .mm-column-title a, ' +
        '.mm-mega a.mm-shop-all, ' +
        '.mm-mega .mm-rail__item, ' +
        '.mm-mega .mm-rail__help, ' +
        '.mm-mega a.mm-category-card'
      );
      if (!link) return;

      const mega = link.closest('.mm-mega');
      if (!mega) return;

      const menuKey = mega.dataset.megaPanel;
      const l1Label = l1LabelFromKey(menuKey);

      // mm_l2 = active L2 tab title (works for L4 links and category cards;
      // for tab-link clicks it'll match the clicked link's own text).
      const activeTab = $('[role="tab"][aria-selected="true"]', mega);
      const activeTabLink = activeTab ? $('.mm-tab-link', activeTab) : null;
      let l2Label = activeTabLink ? activeTabLink.textContent.trim()
                                  : (activeTab ? activeTab.textContent.trim() : null);
      const isTabLink = link.classList.contains('mm-tab');
      if (isTabLink) {
        // For tab clicks, mm_l2 is the tab's own text — even if the user
        // hadn't hovered to activate it yet. Read the label span rather than
        // the button so the chevron SVG can't leak into the string.
        const clickedLabel = $('.mm-tab-link', link);
        l2Label = (clickedLabel || link).textContent.trim();
      }

      let colTitle = null;
      const isCategoryCard = link.classList.contains('mm-category-card');
      const column = link.closest('.mm-column');
      if (isCategoryCard) {
        colTitle = 'category_image';
      } else if (column) {
        const titleEl = $('.mm-column-title', column)  /* tag-agnostic: the heading level is h4 now, was h3 */;
        colTitle = titleEl ? titleEl.textContent.trim() : null;
      }

      // L2 tabs are <button data-tab-url>, everything else is a real <a href>.
      // Without the dataset fallback every tab click would report mm_link_url:''.
      const url = stripQuery(link.getAttribute('href') || link.dataset.tabUrl || '');
      const linkText = isCategoryCard
        ? ($('.mm-category-card__label', link)?.textContent?.trim()
           || (link.getAttribute('aria-label') || '').trim()
           || (link.textContent || '').trim())
        : isTabLink
          ? l2Label
          : (link.textContent || '').trim();
      const level = isTabLink ? 'L2'
                  : (isCategoryCard ? 'L3' : 'L4');

      // Role comes from the markup, not from CSS classes or nesting depth.
      // Extends the EXISTING link_click rather than adding a new action
      // family: renaming mega_open/tab_switch/link_click would break the
      // Clarity routing inside Analytics.track() and every historical series.
      const linkRole = link.getAttribute('data-mm-link-role')
        || (isTabLink ? 'hub' : isCategoryCard ? 'category_card' : 'collection');

      Analytics.track('link_click', {
        mm_menu: menuKey,          // legacy
        mm_level: level,           // legacy
        mm_column: colTitle,
        mm_link_role: linkRole,
        mm_link_text: linkText,
        mm_link_url: url,
        mm_l1: l1Label,
        mm_l2: l2Label,
        mm_device: getDeviceLabel()
      });

      // Mark click + sync-fire close BEFORE the browser navigates.
      // The topture-tracking listener uses sendBeacon — survives unload.
      markClick();
      fireSyncCloseWithClick();
    });

    // ── MOBILE (drawer) ──────────────────────────────────────
    // L1 tab anchors, L2 "Shop All" + L4 items inside accordions, Best
    // Sellers product cards. EXCLUDES .mm-mobile-cta (L1 sidebar CTA),
    // .mm-mobile-cta-card (L0 root CTA cards), .mm-mobile-footer-links a
    // (footer) — those have dedicated events.
    document.addEventListener('click', (e) => {
      const link = e.target.closest(
        '.mm-drawer a.mm-mobile-link, ' +
        '.mm-drawer a.mm-mobile-shop-all, ' +
        '.mm-drawer a.mm-mobile-product-card'
      );
      if (!link) return;

      // Walk up to find the panel this link lives in
      const panel = link.closest('[data-mobile-panel]');
      if (!panel) return;

      // L1/L2 labels come from data attributes set during DOM build
      const l1Label = panel.dataset.l1Label || null;
      const l2Label = panel.dataset.l2Label || null;

      // Column title comes from the MEANING hook, not a presentation class.
      // The old '.mm-mobile-accordion-content' walk died silently when the
      // accordions became static headings and reported mm_column: null for
      // every mobile click. Read the heading exactly as fireMobileImpressions
      // does — visible <h3> first, slug attribute only as a fallback — so the
      // historical human value shape of mm_column is preserved.
      let colTitle = null;
      const group = link.closest('[data-mm-impression-group]');
      if (group) {
        const heading = group.previousElementSibling;
        colTitle = (heading && heading.classList.contains('mm-mobile-group__title'))
          ? (heading.textContent || '').replace(/\s+/g, ' ').trim()
          : (group.getAttribute('data-mm-impression-group') || null);
      }

      const isProductCard = link.classList.contains('mm-mobile-product-card');
      const linkText = isProductCard
        ? ($('.mm-mobile-product-card__title', link)?.textContent?.trim()
           || (link.textContent || '').trim())
        : (link.textContent || '').trim();
      const url = stripQuery(link.getAttribute('href') || '');

      const details = {
        mm_menu: panel.dataset.mobilePanel,        // legacy
        mm_level: 'L' + (panel.dataset.level || ''), // legacy
        mm_column: colTitle,
        mm_link_role: link.getAttribute('data-mm-link-role')
          || (isProductCard ? 'product_card' : 'collection'),
        mm_link_text: linkText,
        mm_link_url: url,
        mm_l1: l1Label,
        mm_l2: l2Label,
        mm_device: 'mobile'
      };

      // Product-card enrichment — discriminator for dashboard isolation.
      // data-section="drawer_root" => root carousel; otherwise => L2 best sellers.
      if (isProductCard) {
        details.mm_section = link.dataset.section || 'best_sellers';
        details.mm_column = null; // cards aren't in a column
        // Root carousel has no L1/L2 context
        if (details.mm_section === 'drawer_root') {
          details.mm_l1 = null;
          details.mm_l2 = null;
        }
      }

      Analytics.track('link_click', details);

      markClick();
      fireSyncCloseWithClick();
    });
  }


  /* ============================================================
     MOBILE: LAZY DOM BUILDER
     Reads #mm-mobile-data JSON and builds the full panel tree.
     Called once on first hamburger tap, then never again.
     ============================================================ */

  let mobileBuilt = false;
  let mobileDataPromise = null;
  let mobileBuildPromise = null;

  /* Data source chain: inline #mm-mobile-data (back-compat) → sessionStorage →
     Section Rendering API fetch of sections/mega-menu-data.liquid. The blob
     was ~66KB of HTML on EVERY page load; now it's fetched once per session,
     prefetched at idle so the first hamburger tap almost never waits. */
  function loadMobileData() {
    if (mobileDataPromise) return mobileDataPromise;
    mobileDataPromise = (function () {
      const dataEl = document.getElementById('mm-mobile-data');
      if (dataEl) {
        try { return Promise.resolve(JSON.parse(dataEl.textContent)); }
        catch (e) { return Promise.reject(e); }
      }
      try {
        const cached = sessionStorage.getItem('mmMobileDataV2');
        if (cached) return Promise.resolve(JSON.parse(cached));
      } catch (e) { /* storage unavailable — fall through to fetch */ }
      return fetch('/?sections=mega-menu-data', { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error('mega-menu-data section fetch ' + r.status);
          return r.json();
        })
        .then((sections) => {
          const doc = new DOMParser().parseFromString(sections['mega-menu-data'] || '', 'text/html');
          const el = doc.getElementById('mm-mobile-data');
          if (!el) throw new Error('mm-mobile-data missing in section render');
          const txt = el.textContent;
          try { sessionStorage.setItem('mmMobileDataV2', txt); } catch (e) { /* quota — fine */ }
          return JSON.parse(txt);
        });
    })();
    mobileDataPromise.catch(() => { mobileDataPromise = null; }); // allow retry
    return mobileDataPromise;
  }

  function buildMobileDOM() {
    if (mobileBuildPromise) return mobileBuildPromise;
    mobileBuildPromise = loadMobileData().then((data) => {
      if (mobileBuilt) return;
      mobileBuilt = true;
      buildMobileDOMFrom(data);
    }).catch((e) => {
      console.error('Mega menu: failed to load mobile data', e);
      mobileBuildPromise = null; // next open retries
      throw e;
    });
    return mobileBuildPromise;
  }

  function buildMobileDOMFrom(data) {
    const content = $('.mm-drawer__content');
    if (!content) return;

    // === ROOT PANEL (L0) ===
    const rootPanel = buildRootPanel(data);
    content.appendChild(rootPanel);

    // === L1 + L2 PANELS (per category) ===
    data.categories.forEach(cat => {
      const l1Panel = buildL1Panel(cat);
      content.appendChild(l1Panel);

      cat.tabs.forEach((tab, tabIdx) => {
        if (tab.hasChildren > 0) {
          const l2Panel = buildL2Panel(cat, tab, tabIdx);
          content.appendChild(l2Panel);
        }
      });
    });

    // Bind all mobile event listeners on the freshly built DOM
    bindMobileDrillDown();
    initCarouselScrollIndicators();

    // Remove the inline JSON blob if this page still had one — no longer needed
    const inlineDataEl = document.getElementById('mm-mobile-data');
    if (inlineDataEl) inlineDataEl.remove();
  }

  /**
   * Always-visible custom scroll indicator for horizontal carousels.
   * Reason for existing: iOS Safari ignores `::-webkit-scrollbar` styling on
   * `-webkit-overflow-scrolling: touch` containers and hides the native bar
   * until the user actually scrolls — defeating the "more content exists"
   * affordance. This injects a 3px track below each carousel with a thumb
   * positioned/sized from scrollLeft/scrollWidth, updated on scroll AND on
   * panel-show (so the L2 Best Sellers carousels render correctly the moment
   * the user drills into a panel where they were previously offscreen-hidden).
   */
  function initCarouselScrollIndicators() {
    const drawer = $('[data-mobile-drawer]');
    if (!drawer) return;
    $$('.mm-mobile-products', drawer).forEach(carousel => {
      if (carousel._mmHasIndicator) return;
      carousel._mmHasIndicator = true;

      const track = h('div', { className: 'mm-mobile-products__indicator' });
      const thumb = h('div', { className: 'mm-mobile-products__indicator-thumb' });
      track.appendChild(thumb);

      // Insert after the carousel (or after its wrapper if one exists, so
      // the indicator sits BELOW the fade-gradient overlay area, not under it)
      const anchor = carousel.closest('.mm-mobile-products-wrap') || carousel;
      anchor.parentNode.insertBefore(track, anchor.nextSibling);

      function update() {
        const sw = carousel.scrollWidth;
        const cw = carousel.clientWidth;
        // Hide if content fits without scrolling
        if (sw <= cw + 2) {
          track.style.display = 'none';
          return;
        }
        track.style.display = '';
        const visibleRatio = cw / sw;
        const maxScroll = sw - cw;
        const scrollRatio = maxScroll > 0 ? carousel.scrollLeft / maxScroll : 0;
        thumb.style.width = (visibleRatio * 100) + '%';
        thumb.style.left = (scrollRatio * (100 - visibleRatio * 100)) + '%';
      }

      carousel.addEventListener('scroll', update, { passive: true });
      // Stash updater so showMobilePanel can refresh after a hidden panel
      // becomes visible (scrollWidth/clientWidth can be wrong while hidden).
      carousel._mmUpdateIndicator = update;
      // Initial paint
      requestAnimationFrame(update);
    });
  }

  /* --- Root panel (L0): categories + CTAs + review + footer --- */
  function buildRootPanel(data) {
    const panel = h('div', {
      className: 'mm-mobile-panel mm-mobile-panel--active',
      'data-mobile-panel': 'root',
      'data-level': '0'
    });

    // Header: "Menu" + close X
    const header = h('div', { className: 'mm-panel-header mm-panel-header--root' });
    header.appendChild(h('span', { className: 'mm-panel-header__root-title', textContent: 'Menu' }));
    header.appendChild(makeCloseButton());
    panel.appendChild(header);

    // Category list
    const ul = h('ul', { className: 'mm-mobile-list mm-mobile-list--root' });
    data.categories.forEach(cat => {
      const li = h('li', { className: 'mm-mobile-item mm-mobile-item--has-children mm-mobile-item--root' });
      const btn = h('button', {
        className: 'mm-mobile-link mm-mobile-link--root',
        'data-mobile-drill': cat.key,
        'aria-expanded': 'false'
      });
      if (cat.image) {
        const img = h('img', {
          src: cat.image,
          alt: cat.label,
          loading: 'lazy',
          width: '60',
          height: '60',
          className: 'mm-mobile-root__img'
        });
        btn.appendChild(img);
      }
      btn.appendChild(h('span', { className: 'mm-mobile-root__label', textContent: cat.label }));
      btn.appendChild(svg(SVG_CHEVRON_RIGHT));
      li.appendChild(btn);
      ul.appendChild(li);
    });
    // Drawer-root product carousel — appended BEFORE the category list so
    // it's the first content users see after the header. Curated handle
    // list from Liquid. Cards reuse .mm-mobile-product-card styling (same
    // visual as Best Sellers) and carry data-section="drawer_root" so
    // link_click and impressions can distinguish them from L2 best_sellers.
    if (data.rootProducts && data.rootProducts.length > 0) {
      // Header row: "Best Sellers" title + left/right scroll arrows on the same line.
      const rootHeader = h('div', { className: 'mm-mobile-products__header mm-mobile-products__header--root' });
      rootHeader.appendChild(h('p', {
        className: 'mm-mobile-products__heading mm-mobile-products__heading--root',
        textContent: 'Best Sellers'
      }));
      const rootArrows = h('div', { className: 'mm-mobile-products__arrows' });
      const rootPrevBtn = h('button', {
        type: 'button',
        className: 'mm-mobile-products__arrow mm-mobile-products__arrow--prev',
        'aria-label': 'Scroll best sellers left',
        'aria-controls': 'mm-root-bestsellers',
        // Start disabled. updateRootArrowState reconciles on scroll/panel-show.
        // Setting at creation avoids the brief "active" flash before the first rAF.
        disabled: ''
      });
      rootPrevBtn.appendChild(svg(SVG_BACK));
      const rootNextBtn = h('button', {
        type: 'button',
        className: 'mm-mobile-products__arrow mm-mobile-products__arrow--next',
        'aria-label': 'Scroll best sellers right',
        'aria-controls': 'mm-root-bestsellers'
      });
      rootNextBtn.appendChild(svg(SVG_CHEVRON_RIGHT));
      rootArrows.appendChild(rootPrevBtn);
      rootArrows.appendChild(rootNextBtn);
      rootHeader.appendChild(rootArrows);
      panel.appendChild(rootHeader);
      // Wrapper exists so the fade-gradient pseudo-element can anchor to a
      // positioned parent without interfering with the scroller's own layout.
      const rootWrap = h('div', { className: 'mm-mobile-products-wrap' });
      const rootProductsDiv = h('div', {
        className: 'mm-mobile-products mm-mobile-products--root',
        id: 'mm-root-bestsellers'
      });
      data.rootProducts.forEach(item => {
        const card = h('a', {
          href: item.url,
          className: 'mm-mobile-product-card mm-mobile-product-card--root',
          'data-section': 'drawer_root'
        });
        if (item.image) {
          const img = h('img', {
            src: item.image,
            alt: item.title,
            loading: 'lazy',
            width: '90',
            height: '90',
            className: 'mm-mobile-product-card__img',
            sizes: '90px'
          });
          const srcsetParts = [];
          if (item.image1x) srcsetParts.push(item.image1x + ' 1x');
          srcsetParts.push(item.image + ' 2x');
          if (item.image3x) srcsetParts.push(item.image3x + ' 3x');
          img.setAttribute('srcset', srcsetParts.join(', '));
          card.appendChild(img);
        }
        card.appendChild(h('span', {
          className: 'mm-mobile-product-card__title',
          textContent: item.title
        }));
        rootProductsDiv.appendChild(card);
      });
      rootWrap.appendChild(rootProductsDiv);
      panel.appendChild(rootWrap);

      // Arrow scroll wiring. Step = ~2 card widths so each tap moves a meaningful
      // chunk of the carousel. Disabled state communicates "no more in that
      // direction" — initial state shows next-enabled to signal scrollable content.
      const ARROW_STEP = 204; // 2 × (90px card + 12px gap)
      rootPrevBtn.addEventListener('click', () => {
        rootProductsDiv.scrollBy({ left: -ARROW_STEP, behavior: 'smooth' });
      });
      rootNextBtn.addEventListener('click', () => {
        rootProductsDiv.scrollBy({ left: ARROW_STEP, behavior: 'smooth' });
      });
      const updateRootArrowState = () => {
        // Threshold of 24px covers the carousel's 20px left-padding which,
        // combined with scroll-snap-type: x mandatory, leaves scrollLeft
        // resting around ~20 on initial layout even though no "previous"
        // content exists. Symmetric for atEnd to handle the right padding.
        const max = rootProductsDiv.scrollWidth - rootProductsDiv.clientWidth;
        const atStart = rootProductsDiv.scrollLeft <= 24;
        const atEnd = rootProductsDiv.scrollLeft >= max - 24;
        rootPrevBtn.toggleAttribute('disabled', atStart);
        rootNextBtn.toggleAttribute('disabled', atEnd || max <= 0);
      };
      rootProductsDiv.addEventListener('scroll', updateRootArrowState, { passive: true });
      // Stash updater so showMobilePanel can refresh after a hidden panel
      // becomes visible (scrollWidth measurements while hidden can be 0).
      rootProductsDiv._mmUpdateArrows = updateRootArrowState;
      // Defer to next frame so layout is settled before measuring.
      requestAnimationFrame(updateRootArrowState);
    }

    panel.appendChild(ul);

    // Aggregate review stats — broad social-proof signal above the testimonial.
    // Format: "Rated 4.96/5 based on 462 customer reviews" (a review count, not a count of owners) with the rating and
    // count values bolded. Whole line single-row, centered, same font size.
    if (data.reviewStats) {
      const stats = h('div', { className: 'mm-mobile-review-stats' });
      stats.appendChild(h('span', {
        className: 'mm-mobile-review-stats__stars',
        textContent: '★★★★★ '
      }));
      stats.appendChild(document.createTextNode('Rated '));
      stats.appendChild(h('strong', {
        className: 'mm-mobile-review-stats__rating',
        textContent: data.reviewStats.avgRating + '/5'
      }));
      stats.appendChild(document.createTextNode(' based on '));
      stats.appendChild(h('strong', {
        className: 'mm-mobile-review-stats__count',
        textContent: data.reviewStats.reviewCount
      }));
      stats.appendChild(document.createTextNode(' customer reviews'));
      panel.appendChild(stats);
    }

    // Review card
    const reviewCard = h('div', {
      className: 'mm-mobile-review-card',
      'data-review-card': '',
      'data-review-index': '0'
    });
    const reviewHeader = h('div', { className: 'mm-mobile-review-card__header' });
    const reviewInfo = h('div');
    reviewInfo.appendChild(h('strong', { className: 'mm-mobile-review-card__name', textContent: 'Richard L.' }));
    const starsDiv = h('div', { className: 'mm-mobile-review-card__stars' });
    const starsSpan = h('span');
    starsSpan.style.color = '#f4b400';
    starsSpan.textContent = '★★★★★';
    starsDiv.appendChild(starsSpan);
    const verifiedSpan = h('span', { className: 'mm-mobile-review-card__verified' });
    verifiedSpan.appendChild(svg(
      '<svg class="mm-mobile-review-card__verified-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">' +
        '<circle cx="8" cy="8" r="8" fill="#19733e" fill-opacity="0.1"/>' +
        '<path d="M4.5 8.25 L7 10.75 L11.5 5.75" stroke="#19733e" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
      '</svg>'
    ));
    verifiedSpan.appendChild(document.createTextNode('Verified Review'));
    starsDiv.appendChild(verifiedSpan);
    reviewInfo.appendChild(starsDiv);
    reviewHeader.appendChild(reviewInfo);
    reviewCard.appendChild(reviewHeader);
    reviewCard.appendChild(h('p', {
      className: 'mm-mobile-review-card__quote',
      textContent: '"Couldn\'t have had a better experience! Thanks so much!!"'
    }));
    panel.appendChild(reviewCard);

    // Footer links
    const footerUl = h('ul', { className: 'mm-mobile-footer-links' });
    data.footerLinks.forEach(link => {
      const li = h('li');
      li.appendChild(h('a', { href: link.url, textContent: link.title }));
      footerUl.appendChild(li);
    });
    panel.appendChild(footerUl);

    return panel;
  }

  /* --- L1 panel: tab list for a category --- */
  function buildL1Panel(cat) {
    const panel = h('div', {
      className: 'mm-mobile-panel',
      'data-mobile-panel': cat.key,
      'data-level': '1',
      'data-l1-label': cat.label || '',
      hidden: true
    });

    // Header: ‹ Back | Title (linked) | ✕ Close
    panel.appendChild(makePanelHeader('root', 'Menu'));
    // Same tinted summary block as L2, so both levels read identically and the
    // centred text CTA has a ground to sit on rather than floating above a
    // bare list. The title used to be the link to this category's collection;
    // now that it is a heading, that destination needs an explicit anchor.
    const l1hero = h('div', { className: 'mm-mobile-hero' });
    l1hero.appendChild(makePanelTitle(cat.label));
    if (cat.url && cat.url !== '/' && cat.url !== '#') {
      l1hero.appendChild(h('a', {
        href: cat.url, className: 'mm-mobile-shop-all',
        textContent: 'Shop All ' + cat.label, 'data-mm-link-role': 'shop_all'
      }));
    }
    panel.appendChild(l1hero);

    const ul = h('ul', { className: 'mm-mobile-list' });
    cat.tabs.forEach((tab, idx) => {
      const li = h('li', { className: 'mm-mobile-item'
        + (tab.hasChildren > 0 ? ' mm-mobile-item--has-children' : '')
        + (tab.groupStart ? ' mm-mobile-item--group-start' : '') });

      if (tab.hasChildren > 0) {
        const btn = h('button', {
          className: 'mm-mobile-link mm-mobile-link--parent',
          'data-mobile-drill': cat.key + '-' + idx,
          'aria-expanded': 'false'
        });
        btn.appendChild(h('span', { textContent: tab.title }));
        btn.appendChild(svg(SVG_CHEVRON_RIGHT_SM));
        li.appendChild(btn);
      } else {
        li.appendChild(h('a', { href: tab.url, className: 'mm-mobile-link', textContent: tab.title }));
      }
      ul.appendChild(li);
    });

    // Sidebar CTA
    if (cat.cta) {
      const ctaLi = h('li', { className: 'mm-mobile-item mm-mobile-item--cta' });
      ctaLi.appendChild(h('a', {
        href: cat.cta.url,
        className: 'mm-mobile-cta',
        textContent: cat.cta.label,
        'data-cta-type': CTA_TYPE_BY_L1_KEY[cat.key] || '',
        'data-cta-location': 'mobile_drawer',
        'data-l1-key': cat.key || ''
      }));
      ul.appendChild(ctaLi);
    }

    panel.appendChild(ul);
    return panel;
  }

  /* --- L2 panel: shop-all, best sellers + flat link groups for a tab --- */
  function buildL2Panel(cat, tab, tabIdx) {
    const panelId = cat.key + '-' + tabIdx;
    const panel = h('div', {
      className: 'mm-mobile-panel',
      'data-mobile-panel': panelId,
      'data-level': '2',
      'data-l1-label': cat.label || '',
      'data-l2-label': tab.title || '',
      'data-l1-key': cat.key || '',
      hidden: true
    });

    panel.appendChild(makePanelHeader(cat.key, cat.label));

    /* The category summary — title, best-sellers strip and the primary CTA —
       lives in one tinted block so it reads as a unit, with the browse groups
       on white below. Hierarchy here comes from FORM, not type size: a plain
       heading, an image strip, a small-caps label and a solid button are four
       different kinds of thing, so none of them competes with the others. The
       previous layout rendered all four roles as text at four sizes, which is
       why the title and the Shop All link read as two headings in a row. */
    const hero = h('div', { className: 'mm-mobile-hero' });
    hero.appendChild(makePanelTitle(tab.title));

    // Best Sellers section
    if (tab.bestSellers && tab.bestSellers.length > 0) {
      // "Best Sellers", not "Best Selling <tab>" — the tab name is in the
      // heading directly above, so repeating it just makes a long small-caps
      // label. mm_section stays the hardcoded 'best_sellers' either way.
      hero.appendChild(h('p', {
        className: 'mm-mobile-products__heading',
        textContent: 'Best Sellers'
      }));

      const productsDiv = h('div', { className: 'mm-mobile-products' });
      tab.bestSellers.forEach(item => {
        const card = h('a', { href: item.url, className: 'mm-mobile-product-card' });
        if (item.image) {
          const img = h('img', {
            src: item.image,
            alt: item.title,
            loading: 'lazy',
            width: '120',
            height: '120',
            className: 'mm-mobile-product-card__img',
            sizes: '120px'
          });
          // Build srcset
          const srcsetParts = [];
          if (item.image1x) srcsetParts.push(item.image1x + ' 1x');
          srcsetParts.push(item.image + ' 2x');
          if (item.image3x) srcsetParts.push(item.image3x + ' 3x');
          img.setAttribute('srcset', srcsetParts.join(', '));
          card.appendChild(img);
        }
        card.appendChild(h('span', { className: 'mm-mobile-product-card__title', textContent: item.title }));
        productsDiv.appendChild(card);
      });
      hero.appendChild(productsDiv);
    }

    /* CTA last, below the cards: a solid button is the only filled element in
       the panel, so it cannot be mistaken for a heading no matter what follows
       it. Anchors navigate; the drill control is a button — this one navigates,
       so it stays an <a> styled as a button. */
    /* Browse-axis tabs ("Saunas by Brand") are pointed at the HOME PAGE in the
       nav editor, so the '/' check below already suppresses this link. Handled
       in the data, not by a second implicit rule here. */
    if (tab.url && tab.url !== '/' && tab.url !== '#') {
      hero.appendChild(h('a', {
        href: tab.url, className: 'mm-mobile-shop-all',
        textContent: 'Shop All ' + tab.title, 'data-mm-link-role': 'shop_all'
      }));
    }
    panel.appendChild(hero);

    // Flat groups. Buttons control menu state, anchors navigate, headings
    // organise — a column title does none of the first two, so it is an <h3>.
    const ul = h('ul', { className: 'mm-mobile-list' });
    tab.columns.forEach(column => {
      const li = h('li', { className: 'mm-mobile-item mm-mobile-group' });

      if (column.links && column.links.length > 0) {
        /* Heading omitted when the tab has exactly ONE column: the panel title
           above already names it, so this would be a second headline saying the
           same thing ("Saunas by Brand" / "SHOP BY BRAND"). tab.columns is
           already free of rail and separator columns, so its length IS the
           taxonomy count. */
        if (tab.columns.length > 1) {
          li.appendChild(h('p', { className: 'mm-mobile-group__title', textContent: column.title }));
        }

        const subUl = h('ul', {
          className: 'mm-mobile-group__links',
          // Analytics attaches to MEANING, not presentation classes.
          'data-mm-impression-group': (column.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
        });
        // Skip a column-level "Shop All" that would duplicate the panel-level one
        // added above. Several columns carry the same url as their parent tab
        // (e.g. "Shop by Size" -> /collections/outdoor-saunas), which rendered
        // "Shop All Outdoor Saunas" twice in the same screen.
        if (column.url && column.url !== tab.url) {
          subUl.appendChild(h('li', {}, h('a', {
            href: column.url, className: 'mm-mobile-link',
            textContent: 'Shop All ' + column.title, 'data-mm-link-role': 'shop_all'
          })));
        }
        column.links.forEach(link => {
          // Some columns contain a hand-authored link to the tab's own
          // collection (e.g. "Shop by Size" holds "Shop All Outdoor Saunas"
          // -> /collections/outdoor-saunas). The panel-level Shop All anchor
          // above now covers that destination, so rendering it again puts the
          // same link twice on one screen. Drop the in-column copy; the
          // panel-level one is higher in the panel and reads as the primary.
          // Desktop is unaffected — it has no panel-level equivalent.
          if (link.url === tab.url) return;
          subUl.appendChild(h('li', {}, h('a', {
            href: link.url, className: 'mm-mobile-link',
            textContent: link.title, 'data-mm-link-role': 'collection'
          })));
        });
        li.appendChild(subUl);
      } else if (column.url) {
        li.appendChild(h('a', {
          href: column.url, className: 'mm-mobile-link',
          textContent: column.title, 'data-mm-link-role': 'collection'
        }));
      } else {
        // Placeholder with no children — text, never a homepage anchor.
        li.appendChild(h('span', { className: 'mm-mobile-group__title', textContent: column.title }));
      }
      ul.appendChild(li);
    });
    panel.appendChild(ul);

    return panel;
  }

  /* --- Helpers for panel headers and close buttons --- */
  function makeCloseButton() {
    const btn = h('button', {
      className: 'mm-panel-header__close',
      'aria-label': 'Close menu',
      'data-mobile-close': ''
    });
    btn.appendChild(svg(SVG_CLOSE));
    return btn;
  }

  /* Header row: a LABELLED back control plus close.
     The back button names its DESTINATION ("Saunas"), not the current panel —
     so the row answers "where does back take me", while the title below answers
     "where am I". Previously the only text in the header was the current
     panel's own name sitting next to an unlabelled arrow, which read as if the
     arrow went to that name. The label was already computed for aria-label;
     it is now visible too. */
  function makePanelHeader(backTarget, backLabel) {
    const header = h('div', { className: 'mm-panel-header' });

    const backBtn = h('button', {
      className: 'mm-panel-header__back',
      'data-mobile-back': backTarget,
      'aria-label': backLabel ? 'Back to ' + backLabel : 'Back'
    });
    backBtn.appendChild(svg(SVG_BACK));
    if (backLabel) {
      backBtn.appendChild(h('span', { className: 'mm-panel-header__back-label', textContent: backLabel }));
    }
    header.appendChild(backBtn);
    header.appendChild(makeCloseButton());
    return header;
  }

  /* Current location, below the header row. A heading, not a link — the
     "Shop All <title>" anchor directly beneath carries the destination, so
     making this tappable too would put the same link twice in a row. */
  function makePanelTitle(title) {
    return h('p', { className: 'mm-panel-title', textContent: title });
  }


  /* ============================================================
     MOBILE: EVENT BINDING (called after DOM build)
     ============================================================ */

  function bindMobileDrillDown() {
    const drawer = $('[data-mobile-drawer]');
    if (!drawer) return;

    // Use event delegation on the drawer for drill + back buttons
    drawer.addEventListener('click', (e) => {
      // Drill into sub-panel
      const drillBtn = e.target.closest('[data-mobile-drill]');
      if (drillBtn) {
        const target = drillBtn.dataset.mobileDrill;
        showMobilePanel(target);
        drillBtn.setAttribute('aria-expanded', 'true');
        Analytics.track('mobile_drill', {
          mm_target: target,
          mm_label: drillBtn.textContent.trim()
        });
        // L1 drill = "user opened this category panel" — mirror desktop
        // mega_open so the dashboard can group opens/bounce by mm_l1 across
        // devices. L2 drill (target contains a hyphen) is not a panel open;
        // showMobilePanel already fires menu_impressions for that.
        const isL1Drill = target && target.indexOf('-') === -1;
        if (isL1Drill) {
          Analytics.track('mega_open', {
            mm_menu: target,                  // legacy
            mm_l1: l1LabelFromKey(target),
            mm_device: 'mobile'
          });
        }
        return;
      }

      // Back button
      const backBtn = e.target.closest('[data-mobile-back]');
      if (backBtn) {
        const target = backBtn.dataset.mobileBack;
        showMobilePanel(target, true);
        Analytics.track('mobile_back', { mm_target: target });
        return;
      }
    });
  }

  // Note: Close buttons are handled via event delegation in initMobileDrawer()
  // — the drawer's click handler catches [data-mobile-close] clicks.


  /* ============================================================
     MOBILE: DRAWER OPEN / CLOSE
     ============================================================ */

  let scrollPos = 0;

  function initMobileDrawer() {
    const toggleBtn = $('[data-mobile-toggle]');
    const drawer = $('[data-mobile-drawer]');
    const overlay = $('[data-mobile-overlay]');

    if (!toggleBtn || !drawer) return;

    toggleBtn.addEventListener('click', () => {
      const isOpen = drawer.classList.contains('mm-drawer--open');
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    // Prefetch the menu data at idle on mobile viewports so the first
    // hamburger tap never waits on the network (once per session — cached
    // in sessionStorage). Desktop never fetches it.
    if (window.matchMedia && window.matchMedia('(max-width: 999px)').matches) {
      const idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 2000); };
      idle(() => { loadMobileData().catch(() => {}); });
    }

    // Close button delegation — works on lazily built DOM
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-close]')) {
        closeDrawer();
      }
    });

    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Swipe to close — skip if the touch started inside a horizontal scroller
    // (e.g., the .mm-mobile-products carousel on root or L2 Best Sellers).
    // Without this guard, scrolling the carousel sideways is misinterpreted
    // as a swipe-close gesture and closes the entire drawer.
    let touchStartX = 0;
    let touchStartedInScroller = false;
    drawer.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartedInScroller = !!(e.target.closest && e.target.closest('.mm-mobile-products'));
    }, { passive: true });

    drawer.addEventListener('touchend', (e) => {
      if (touchStartedInScroller) return;
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;
      if (diff > 80) {
        closeDrawer();
      }
    }, { passive: true });

    function openDrawer() {
      // Visual open is immediate; the DOM-dependent steps run once the lazy
      // build resolves. Data is prefetched at idle, so in practice the .then
      // fires in the same tick and the drawer looks identical to before.
      scrollPos = window.scrollY;
      document.body.classList.add('mm-no-scroll');
      document.body.style.top = `-${scrollPos}px`;

      drawer.classList.add('mm-drawer--open');
      drawer.removeAttribute('hidden');
      toggleBtn.setAttribute('aria-expanded', 'true');

      if (overlay) {
        overlay.classList.add('mm-overlay--visible');
        overlay.setAttribute('aria-hidden', 'false');
      }

      buildMobileDOM().then(() => {
        if (!drawer.classList.contains('mm-drawer--open')) return; // closed while loading

        // Start a fresh menu session BEFORE showMobilePanel so session_id is
        // injected on any impressions/events fired during initial panel show.
        // mm_l1 is null on root; updated when user drills into a category.
        startMenuSession(null);

        // Fire impressions for the drawer-root Quiz / Calculator CTA cards —
        // without these, those CTAs have a NULL CTR denominator.
        fireDrawerRootImpressions(drawer);

        // Reset to root panel
        showMobilePanel('root');

        // Init review observer (idempotent — safe to call on every open)
        initReviewTracking(drawer);

        // Focus trap setup
        requestAnimationFrame(() => {
          const firstFocusable = $('button, a, [tabindex]', drawer);
          if (firstFocusable) firstFocusable.focus();
        });
      }).catch(() => { /* data fetch failed — next open retries */ });

      // Hide Crisp chat widget so it doesn't overlay the drawer
      if (window.$crisp) {
        try { window.$crisp.push(['do', 'chat:hide']); } catch (e) {}
      }

      // Analytics — legacy event preserved
      Analytics.track('mobile_drawer_open', {
        mm_device: 'mobile'
      });
    }

    function closeDrawer() {
      // Fire menu_close_with_click / menu_close_no_click BEFORE clearing
      // session state. No-op if a sync close already cleared the session
      // (e.g. user tapped a link which triggered sync close).
      endMenuSession();

      // Tear down review observer (idempotent — guard checks reviewObserver)
      teardownReviewTracking();

      drawer.classList.remove('mm-drawer--open');
      toggleBtn.setAttribute('aria-expanded', 'false');

      if (overlay) {
        overlay.classList.remove('mm-overlay--visible');
        overlay.setAttribute('aria-hidden', 'true');
      }

      document.body.classList.remove('mm-no-scroll');
      document.body.style.top = '';
      window.scrollTo(0, scrollPos);

      toggleBtn.focus();

      // Restore Crisp chat widget
      if (window.$crisp) {
        try { window.$crisp.push(['do', 'chat:show']); } catch (e) {}
      }

      // Legacy event preserved
      Analytics.track('mobile_drawer_close', {
        mm_device: 'mobile'
      });
    }

    // Focus trap
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = $$('button:not([hidden]):not([disabled]), a:not([hidden]), [tabindex]:not([hidden]):not([tabindex="-1"])', drawer)
        .filter(el => el.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }


  /* ============================================================
     MOBILE: PANEL NAVIGATION
     ============================================================ */

  function showMobilePanel(panelId, isBack) {
    const allPanels = $$('[data-mobile-panel]');
    const targetPanel = $(`[data-mobile-panel="${panelId}"]`);

    if (!targetPanel) return;

    allPanels.forEach(p => {
      p.classList.remove('mm-mobile-panel--active', 'mm-mobile-panel--exit-left');
      p.setAttribute('hidden', '');
    });

    targetPanel.classList.add('mm-mobile-panel--active');
    targetPanel.removeAttribute('hidden');

    // Scroll drawer content to top
    const content = targetPanel.closest('.mm-drawer__content');
    if (content) content.scrollTop = 0;

    // Track the L1 the user is currently exploring (drives mm_l1 on close events)
    if (currentMenuSessionId) {
      const level = parseInt(targetPanel.dataset.level, 10);
      if (level === 0) currentMenuL1Key = null;
      else if (level === 1) currentMenuL1Key = panelId; // panelId IS the cat.key on L1
      else if (level === 2) currentMenuL1Key = targetPanel.dataset.l1Key || currentMenuL1Key;
    }

    // Fire impressions when an L2 view becomes visible (mirrors desktop tab_switch)
    if (targetPanel.dataset.level === '2') {
      fireMobileImpressions(targetPanel);
    }

    // Refresh scroll indicators inside the newly-visible panel — scrollWidth
    // measurements while a panel is `hidden` can return stale/zero values
    // depending on the browser.
    $$('.mm-mobile-products', targetPanel).forEach(c => {
      if (typeof c._mmUpdateIndicator === 'function') c._mmUpdateIndicator();
      if (typeof c._mmUpdateArrows === 'function') c._mmUpdateArrows();
    });
  }


  /* ============================================================
     REVIEW CARD VIEW TRACKING (mobile drawer)
     Fires `mobile_review_view` once per card per drawer-open session.
     Uses IntersectionObserver (NOT a scroll handler).
     ============================================================ */

  function initReviewTracking(drawerEl) {
    if (reviewObserver) return;            // already initialized this drawer-open
    const cards = drawerEl.querySelectorAll('[data-review-card]');
    if (!cards.length) return;             // nothing to observe yet
    reviewObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const idx = e.target.dataset.reviewIndex || '0';
        if (seenReviews.has(idx)) return;
        seenReviews.add(idx);
        Analytics.track('mobile_review_view', {
          review_index: Number(idx),
          mm_device: 'mobile'
        });
      });
    }, { threshold: 0.5 });
    cards.forEach(card => reviewObserver.observe(card));
  }

  function teardownReviewTracking() {
    if (reviewObserver) {
      reviewObserver.disconnect();
      reviewObserver = null;
    }
    seenReviews.clear();
  }


  /* ============================================================
     SIDEBAR CTA TRACKING (desktop + mobile drawer)
     Quiz / Calculator CTAs. Excluded from initLinkTracking — dedicated
     event so dashboard can isolate them from regular L4 link_clicks.
     ============================================================ */

  function initSidebarCtaTracking() {
    document.addEventListener('click', (e) => {
      const cta = e.target.closest(
        '.mm-sidebar-cta, ' +              // desktop panel sidebar
        '.mm-drawer .mm-mobile-cta'        // mobile L1 panel sidebar CTA
      );
      if (!cta) return;

      // Determine mm_l1, cta_type, location
      let l1Key = cta.dataset.l1Key || null;
      let location;
      let ctaType = cta.dataset.ctaType || null;

      if (cta.classList.contains('mm-sidebar-cta')) {
        // Desktop: derive l1Key from the closest .mm-mega
        const mega = cta.closest('.mm-mega');
        l1Key = mega ? mega.dataset.megaPanel : null;
        location = 'desktop_sidebar';
        if (!ctaType) ctaType = CTA_TYPE_BY_L1_KEY[l1Key] || null;
      } else {
        // Mobile L1 panel sidebar CTA
        location = cta.dataset.ctaLocation || 'mobile_drawer';
        if (!ctaType && l1Key) ctaType = CTA_TYPE_BY_L1_KEY[l1Key] || null;
      }

      const labelEl = cta.querySelector('.mm-sidebar-cta__label, .mm-mobile-cta-card__title');
      const linkText = labelEl ? labelEl.textContent.trim()
                              : (cta.textContent || '').replace(/\s+/g, ' ').trim();
      const url = stripQuery(cta.getAttribute('href') || '');

      Analytics.track('sidebar_cta_click', {
        mm_l1: l1LabelFromKey(l1Key),
        cta_type: ctaType,
        mm_link_text: linkText,
        mm_link_url: url,
        mm_device: getDeviceLabel(),
        location: location
      });

      markClick();
      fireSyncCloseWithClick();
    });
  }


  /* ============================================================
     HEADER + DRAWER FOOTER CLICKS (cart, account, logo, footer)
     Outside-the-menu navigation that we want first-party visibility on.
     ============================================================ */

  function initHeaderClickTracking() {
    // Cart icon (header)
    document.addEventListener('click', (e) => {
      const cart = e.target.closest('.site-header-cart--button');
      if (!cart) return;
      Analytics.track('cart_icon_click', { mm_device: getDeviceLabel() });
      markClick();
      fireSyncCloseWithClick();
    });

    // Account links (header + drawer)
    document.addEventListener('click', (e) => {
      const acct = e.target.closest(
        '.site-header_account-link-anchor, ' +
        '.site-header__account-link--account, ' +
        '.site-header__account-link--logout'
      );
      if (!acct) return;
      const inDrawer = !!acct.closest('.mm-drawer');
      Analytics.track('account_click', {
        location: inDrawer ? 'mobile_drawer' : 'header',
        mm_device: getDeviceLabel()
      });
      markClick();
      fireSyncCloseWithClick();
    });

    // Logo (header)
    document.addEventListener('click', (e) => {
      const logo = e.target.closest('.site-logo');
      if (!logo) return;
      Analytics.track('logo_click', { mm_device: getDeviceLabel() });
      markClick();
      fireSyncCloseWithClick();
    });

    // Mobile drawer footer links (Reviews, About Us, etc.)
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.mm-drawer .mm-mobile-footer-links a');
      if (!link) return;
      Analytics.track('mobile_drawer_footer_click', {
        mm_link_text: (link.textContent || '').trim(),
        mm_link_url: stripQuery(link.getAttribute('href') || ''),
        mm_device: 'mobile'
      });
      markClick();
      fireSyncCloseWithClick();
    });
  }


  /* ============================================================
     SEARCH SUBMISSIONS — fallback-detection signal
     Strongest signal of "menu failed → user searched": followed_menu_close_no_click

     Two-phase dispatch: submit time can't know the result count (results
     don't exist yet — result_count was null on 100% of captured events), so
     the submit handlers STASH the metadata in sessionStorage and the event
     fires ON THE RESULTS PAGE, where theme.liquid exposes
     {{ search.results_count }} as window.__mmSearchResultsCount. Exactly one
     search_submit fires per search; the stash is consumed on read. All
     time-sensitive fields (ms_since_menu_close, followed_menu_close_no_click,
     menu_session_id, mm_device) are captured at SUBMIT time and carried over
     verbatim — the results page has no menu session to enrich from.
     ============================================================ */

  const SEARCH_STASH_KEY = 'mmSearchSubmitV1';
  const SEARCH_STASH_TTL_MS = 120000; // discard if the results page never loaded

  function initSearchTracking() {
    function stashSearchSubmit(rawQuery, location) {
      const q = (rawQuery || '').trim().slice(0, 200);
      if (!q) return;
      if (q.includes('@')) return;
      if (/password|passwd/i.test(q)) return;

      const lastCloseAt = lastMenuCloseAt;
      const lastCloseNoClick = lastMenuCloseHadNoClick;
      const msSince = lastCloseAt ? (Date.now() - lastCloseAt) : null;
      const isFallback = msSince !== null
        && msSince < MENU_FALLBACK_SEARCH_WINDOW_MS
        && lastCloseNoClick === true;

      const payload = {
        query: q,
        query_length: q.length,
        mm_device: getDeviceLabel(),
        location: location,
        ms_since_menu_close: msSince,
        followed_menu_close_no_click: isFallback
      };
      // Capture the session id NOW — Analytics.track's auto-injection reads
      // the CURRENT page's session, which is gone after navigation.
      if (currentMenuSessionId) payload.menu_session_id = currentMenuSessionId;

      try {
        sessionStorage.setItem(SEARCH_STASH_KEY, JSON.stringify({ t: Date.now(), payload: payload }));
      } catch (e) {
        // Storage unavailable (private mode/quota) — fire immediately with the
        // legacy null count rather than losing the event entirely.
        Analytics.track('search_submit', Object.assign({ result_count: null }, payload));
      }
    }

    // Form submit — covers header live-search + results-page search form
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-live-search-form]');
      if (!form) return;
      const input = form.querySelector('input[name="q"]') ||
                    form.querySelector('[data-live-search-input]');
      const q = input ? input.value : '';
      const isResultsPageForm = form.classList.contains('productgrid--search-form');
      const inDrawer = !!form.closest('.mm-drawer');
      const location = inDrawer ? 'mobile_drawer'
                     : (isResultsPageForm ? 'results_page' : 'header');
      stashSearchSubmit(q, location);
    });

    // Predictive-search "see all results" link — navigates to /search?q=...
    // These are inside .search-flydown and are clicks, not form submits.
    document.addEventListener('click', (e) => {
      const a = e.target.closest('.search-flydown a[href*="/search?q="]');
      if (!a) return;
      try {
        const url = new URL(a.getAttribute('href'), window.location.origin);
        const q = url.searchParams.get('q') || '';
        stashSearchSubmit(q, 'header');
      } catch (_) { /* noop */ }
    });
  }

  /* Results-page half of the two-phase dispatch: consume the stash and fire
     the single search_submit with the REAL result count. No stash (direct
     /search visits, bookmarks, crawlers) → no event, matching the old
     submit-only behavior. */
  function fireStashedSearchSubmit() {
    let raw = null;
    try { raw = sessionStorage.getItem(SEARCH_STASH_KEY); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(SEARCH_STASH_KEY); } catch (e) { /* still fire once */ }

    let stash;
    try { stash = JSON.parse(raw); } catch (e) { return; }
    if (!stash || !stash.payload || typeof stash.payload.query !== 'string') return;
    if (!stash.t || (Date.now() - stash.t) > SEARCH_STASH_TTL_MS) return;

    // Only a performed-search page defines the count (theme.liquid).
    if (typeof window.__mmSearchResultsCount !== 'number') return;

    // The count must belong to the stashed query — if the terms differ
    // (edited URL, filtered re-search), discard rather than report a wrong count.
    const pageTerms = String(window.__mmSearchTerms || '').trim().toLowerCase();
    const stashQ = stash.payload.query.trim().toLowerCase();
    if (pageTerms && pageTerms !== stashQ) return;

    const detail = Object.assign({}, stash.payload, {
      result_count: window.__mmSearchResultsCount
    });

    // The topture-tracking snippet loads via theme.liquid's interaction-
    // triggered loader (first interaction or a 5s safety timeout) — firing at
    // DOM-ready would dispatch BEFORE its mm:analytics listener exists and the
    // event would be silently lost. Wait for the loader's ready signal, with a
    // 15s fallback so the dataLayer/GA4 paths still fire if the snippet never
    // loads (ad blockers) — those consumers are page-global and lose nothing.
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      Analytics.track('search_submit', detail);
    };
    if (window.__toptureTrackingLoaded) {
      fire();
    } else {
      document.addEventListener('topture:tracking-loaded', fire, { once: true });
      setTimeout(fire, 15000);
    }
  }


  /* ============================================================
     EXPOSE STATE for downstream consumers (search-fallback detection)
     ============================================================ */

  function exposeMenuState() {
    Object.defineProperty(window, '__mmState', {
      value: {
        get lastMenuCloseAt() { return lastMenuCloseAt; },
        get lastMenuCloseHadNoClick() { return lastMenuCloseHadNoClick; },
        get currentMenuSessionId() { return currentMenuSessionId; }
      },
      writable: false,
      configurable: false
    });
  }


  /* ============================================================
     INIT
     ============================================================ */

  function init() {
    // Signal that JS is active — CSS uses [data-js-ready] to hide
    // non-active panels. Without JS, all panels stay visible.
    $$('.mm-mega').forEach(m => m.setAttribute('data-js-ready', ''));

    initDesktopMega();
    initDesktopTabs();
    initLinkTracking();
    initSidebarCtaTracking();
    initHeaderClickTracking();
    initSearchTracking();
    fireStashedSearchSubmit();
    initMobileDrawer();
    exposeMenuState();
    // NOTE: initMobileDrillDown() is no longer called here — it's bound
    // after buildMobileDOM() on first open. (The accordion initialiser it
    // used to sit beside is gone: mobile groups are static headings now.)
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
